import { TabPanel } from '@headlessui/react';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetVehicleDetails from '../../../api/vehicles/useGetVehicleDetails';
import usePutVehicle from '../../../api/vehicles/usePutVehicle';
import useGetFactories from '../../../api/factories/useGetFactories';
import InputText from '../../../components/agv-forms-input';
import VehicleColorPicker from '../components/vehicle-color-picker';
import VehicleTypeRadio from '../components/vehicle-type-radio';
import FieldTitle from '../../../components/agv-forms-title';

const Details = forwardRef(({ isFocused, vehicleId, vehicleKey, onSavingChange }, ref) => {
  const {
    data,
    isLoading,
    refetch,
  } = useGetVehicleDetails({
    vehicleId,
  });

  const { isLoading: isSaving, putVehicle } = usePutVehicle();
  const { factories } = useGetFactories();

  // Notifier le parent quand isSaving change
  useEffect(() => {
    if (onSavingChange) {
      onSavingChange(isSaving);
    }
  }, [isSaving, onSavingChange]);

  const [vehicleData, setVehicleData] = useState({
    CAMION: '',
    LIBCAM: '',
    IMMAT: '',
    TELCAM: '',
    LIBTYP: '',
    TRANSP: '',
    color: '#3B82F6', // Bleu par défaut
    factory_id: '',
    total_capacity: '',
    linear_capacity: '',
    containers: [],
    // VROOM optimization fields
    profile: 'car', // Valeur par défaut
    costs_per_km: '',
    max_travel_time: '',
    max_distance: '',
    speed_factor: '',
    time_window_start: '',
    time_window_end: '',
    breaks: [],
    skills: [],
  });

  useEffect(() => {
    if (!data && isFocused) {
      refetch();
    }
  }, [isFocused]);

  useEffect(() => {
    if (data?.management) {
      setVehicleData({
        CAMION: data.management.vehicule_key || '',
        LIBCAM: data.management.label || '',
        IMMAT: data.management.imat || '',
        TELCAM: data.management.phone_number || '',
        LIBTYP: data.management.vehicle_type || '',
        TRANSP: '', // À compléter si disponible dans data
        color: data.management.color || '#3B82F6',
        factory_id: data.management.factory_id || '',
        total_capacity: data.management.total_capacity || '',
        linear_capacity: data.management.linear_capacity || '',
        containers: data.management.containers || [],
        // VROOM optimization fields
        profile: data.management.profile || 'car',
        costs_per_km: data.management.costs_per_km || '',
        max_travel_time: data.management.max_travel_time || '',
        max_distance: data.management.max_distance || '',
        speed_factor: data.management.speed_factor || '',
        time_window_start: data.management.time_window_start || '',
        time_window_end: data.management.time_window_end || '',
        breaks: data.management.breaks || [],
        skills: data.management.skills || [],
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setVehicleData({ ...vehicleData, [field]: value });
  };

  const handleContainerChange = (index, field, value) => {
    const updatedContainers = [...vehicleData.containers];
    updatedContainers[index] = {
      ...updatedContainers[index],
      [field]: value,
    };
    setVehicleData({ ...vehicleData, containers: updatedContainers });
  };

  // Gestion des breaks
  const handleAddBreak = () => {
    const newBreak = {
      time_window_start: '08:00',
      time_window_end: '08:30',
      service: 1800, // 30 minutes par défaut
      description: 'Pause',
    };
    setVehicleData({ ...vehicleData, breaks: [...vehicleData.breaks, newBreak] });
  };

  const handleBreakChange = (index, field, value) => {
    const updatedBreaks = [...vehicleData.breaks];
    updatedBreaks[index] = {
      ...updatedBreaks[index],
      [field]: value,
    };
    setVehicleData({ ...vehicleData, breaks: updatedBreaks });
  };

  const handleRemoveBreak = (index) => {
    const updatedBreaks = vehicleData.breaks.filter((_, i) => i !== index);
    setVehicleData({ ...vehicleData, breaks: updatedBreaks });
  };

  // Gestion des skills
  const handleAddSkill = () => {
    const newSkill = {
      skill_id: 1,
      description: '',
    };
    setVehicleData({ ...vehicleData, skills: [...vehicleData.skills, newSkill] });
  };

  const handleSkillChange = (index, field, value) => {
    const updatedSkills = [...vehicleData.skills];
    updatedSkills[index] = {
      ...updatedSkills[index],
      [field]: value,
    };
    setVehicleData({ ...vehicleData, skills: updatedSkills });
  };

  const handleRemoveSkill = (index) => {
    const updatedSkills = vehicleData.skills.filter((_, i) => i !== index);
    setVehicleData({ ...vehicleData, skills: updatedSkills });
  };

  // Calculer automatiquement la capacité totale à partir des containers
  useEffect(() => {
    if (vehicleData.containers && vehicleData.containers.length > 0) {
      const totalCapacity = vehicleData.containers.reduce((sum, container) => {
        const capacity = parseFloat(container.capacity) || 0;
        const unit = container.capacity_unit;
        
        // Convertir en KG si nécessaire
        let capacityInKg = capacity;
        if (unit === 'TONNE') {
          capacityInKg = capacity * 1000;
        } else if (unit === 'KG') {
          capacityInKg = capacity;
        } else if (unit === 'LITRE') {
          // Pour les litres, on garde la valeur telle quelle (densité approximative de 1)
          capacityInKg = capacity;
        }
        
        return sum + capacityInKg;
      }, 0);
      
      // Mettre à jour la capacité totale uniquement si elle a changé
      if (totalCapacity !== parseFloat(vehicleData.total_capacity)) {
        setVehicleData(prev => ({ ...prev, total_capacity: totalCapacity.toString() }));
      }
    }
  }, [vehicleData.containers]);

  const handleSave = async () => {
    try {
      await putVehicle(vehicleKey, {
        label: vehicleData.LIBCAM,
        imat: vehicleData.IMMAT,
        phone_number: vehicleData.TELCAM,
        vehicle_type: vehicleData.LIBTYP,
        color: vehicleData.color,
        factory_id: vehicleData.factory_id ? parseInt(vehicleData.factory_id, 10) : undefined,
        total_capacity: vehicleData.total_capacity ? parseFloat(vehicleData.total_capacity) : undefined,
        linear_capacity: vehicleData.linear_capacity ? parseFloat(vehicleData.linear_capacity) : undefined,
        containers: vehicleData.containers.map(container => ({
          id: container.id,
          container_key: container.container_key,
          capacity: parseFloat(container.capacity),
          capacity_unit: container.capacity_unit,
        })),
        // VROOM optimization fields
        profile: 'car', // Toujours "car" par défaut
        costs_per_km: vehicleData.costs_per_km ? parseFloat(vehicleData.costs_per_km) : undefined,
        max_travel_time: vehicleData.max_travel_time ? parseInt(vehicleData.max_travel_time, 10) : undefined,
        max_distance: vehicleData.max_distance ? parseInt(vehicleData.max_distance, 10) : undefined,
        speed_factor: vehicleData.speed_factor ? parseFloat(vehicleData.speed_factor) : undefined,
        time_window_start: vehicleData.time_window_start || undefined,
        time_window_end: vehicleData.time_window_end || undefined,
        breaks: vehicleData.breaks,
        skills: vehicleData.skills,
      });
      refetch();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Exposer la fonction handleSave et l'état isSaving au parent via ref
  useImperativeHandle(ref, () => ({
    handleSave,
    isSaving,
  }));

  return (
    <TabPanel>
      <div className="tw-mt-8 tw-grid tw-grid-cols-2 tw-gap-6 tw-overflow-x-auto">
        {/* Colonne gauche : Informations générales */}
        <div>
          <div className="modal-label-group">
            <div className="modal-label-title">Informations générales</div>
            <div className="modal-label-description">
              Consultez et modifiez les informations du véhicule
            </div>
          </div>

          {isLoading ? (
            <div className="tw-mt-4 tw-space-y-4">
              <SkeletonLoader width="100%" height={40} style={{ borderRadius: 4 }} />
              <SkeletonLoader width="100%" height={40} style={{ borderRadius: 4 }} />
              <SkeletonLoader width="100%" height={40} style={{ borderRadius: 4 }} />
            </div>
          ) : (
            <div className="tw-mt-4 tw-space-y-4">
              <InputText
                fieldTitle="Numéro du camion"
                value={vehicleData.CAMION}
                setValue={(value) => handleChange('CAMION', value)}
                disabled={true}
                placeholder="Ex: 123"
              />

              <InputText
                fieldTitle="Libellé du camion"
                value={vehicleData.LIBCAM}
                setValue={(value) => handleChange('LIBCAM', value)}
                placeholder="Ex: Camion frigorifique"
              />

              <InputText
                fieldTitle="Immatriculation"
                value={vehicleData.IMMAT}
                setValue={(value) => handleChange('IMMAT', value)}
                placeholder="Ex: AA-123-BB"
              />

              <InputText
                fieldTitle="Téléphone du camion"
                value={vehicleData.TELCAM}
                setValue={(value) => handleChange('TELCAM', value)}
                placeholder="Ex: 0612345678"
              />

              <div>
              <FieldTitle
                title={"Capacités du véhicule"}
                tooltip={""}
                />
                <select
                  value={vehicleData.factory_id}
                  onChange={(e) => handleChange('factory_id', e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-primary focus:tw-border-primary tw-text-sm"
                >
                  <option value="">Sélectionner un site</option>
                  {Array.isArray(factories) && factories.map((factory) => (
                    <option key={factory.id} value={factory.id}>
                      {factory.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tw-mt-4">
                <VehicleTypeRadio
                  fieldTitle="Type de véhicule"
                  value={vehicleData.LIBTYP}
                  setValue={(value) => handleChange('LIBTYP', value)}
                />
              </div>

              <div className="tw-mt-4">
                <VehicleColorPicker
                  fieldTitle="Couleur du véhicule"
                  value={vehicleData.color}
                  setValue={(value) => handleChange('color', value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite : Capacités et Containers */}
        <div className="tw-space-y-6">
          {!isLoading && (
            <div>
              <div className="modal-label-group">
                <div className="modal-label-title">Capacités du véhicule</div>
                <div className="modal-label-description">
                  Capacité totale et mètres linéaires disponibles
                </div>
              </div>
              <div className="tw-mt-4 tw-space-y-4">
                <InputText
                  fieldTitle="Capacité totale (KGS)"
                  value={vehicleData.total_capacity}
                  setValue={(value) => handleChange('total_capacity', value)}
                  placeholder="Calculé automatiquement"
                  type="number"
                  disabled={true}
                />
                <InputText
                  fieldTitle="Mètres linéaires"
                  value={vehicleData.linear_capacity}
                  setValue={(value) => handleChange('linear_capacity', value)}
                  placeholder="Ex: 13.6"
                  type="number"
                  step="0.1"
                />
              </div>
            </div>
          )}

          {!isLoading && vehicleData.containers && Array.isArray(vehicleData.containers) && vehicleData.containers.length > 0 && (
            <div>
              <div className="modal-label-group">
                <div className="modal-label-title">Containers / Compartiments</div>
                <div className="modal-label-description">
                  Configuration des containers du véhicule ({vehicleData.containers.length} container{vehicleData.containers.length > 1 ? 's' : ''})
                </div>
              </div>
              <div className="tw-mt-4">
                <div className="tw-space-y-4">
                  {vehicleData.containers.map((container, index) => (
                    <div
                      key={container.id || index}
                      className="tw-p-4 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg"
                    >
                      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                        <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-bg-indigo-100 tw-text-indigo-600 tw-font-bold tw-text-sm">
                          {index + 1}
                        </div>
                        <div className="tw-text-sm tw-font-medium tw-text-gray-700">
                          {container.container_key || `Container ${index + 1}`}
                        </div>
                      </div>
                      <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                        <InputText
                          fieldTitle="Capacité"
                          value={container.capacity}
                          setValue={(value) => handleContainerChange(index, 'capacity', value)}
                          placeholder="Ex: 8500"
                          type="number"
                        />
                        <div>
                          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                            Unité
                          </label>
                          <select
                            value={container.capacity_unit}
                            onChange={(e) => handleContainerChange(index, 'capacity_unit', e.target.value)}
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-primary focus:tw-border-primary tw-text-sm"
                          >
                            <option value="TONNE">TONNE</option>
                            <option value="KG">KG</option>
                            <option value="LITRE">LITRE</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section VROOM - Optimisation de tournées */}
      <div className="tw-mt-8 tw-border-t tw-pt-6">
        <div className="modal-label-group">
          <div className="modal-label-title">Paramètres d'Optimisation</div>
          <div className="modal-label-description">
            Configuration pour l'optimisation des tournées avec VROOM
          </div>
        </div>

        {!isLoading && (
          <div className="tw-mt-6 tw-grid tw-grid-cols-3 tw-gap-6">
            {/* Colonne 1: Coûts et vitesse */}
            <div className="tw-space-y-4">
              <InputText
                fieldTitle="Coût par km (€)"
                value={vehicleData.costs_per_km}
                setValue={(value) => handleChange('costs_per_km', value)}
                placeholder="Ex: 1.5"
                type="number"
                step="0.01"
              />

              <InputText
                fieldTitle="Facteur de vitesse"
                value={vehicleData.speed_factor}
                setValue={(value) => handleChange('speed_factor', value)}
                placeholder="Ex: 1.0 (0-2)"
                type="number"
                step="0.1"
              />
            </div>

            {/* Colonne 2: Temps et distance */}
            <div className="tw-space-y-4">
              <InputText
                fieldTitle="Temps de trajet max (heures)"
                value={vehicleData.max_travel_time ? (vehicleData.max_travel_time / 3600).toString() : ''}
                setValue={(value) => handleChange('max_travel_time', value ? (parseFloat(value) * 3600).toString() : '')}
                placeholder="Ex: 8"
                type="number"
                step="0.5"
              />

              <InputText
                fieldTitle="Distance max (kilomètres)"
                value={vehicleData.max_distance ? (vehicleData.max_distance / 1000).toString() : ''}
                setValue={(value) => handleChange('max_distance', value ? (parseFloat(value) * 1000).toString() : '')}
                placeholder="Ex: 500"
                type="number"
                step="1"
              />
            </div>

            {/* Colonne 3: Fenêtre horaire */}
            <div className="tw-space-y-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Heure de début
                </label>
                <input
                  type="time"
                  value={vehicleData.time_window_start}
                  onChange={(e) => handleChange('time_window_start', e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-primary focus:tw-border-primary tw-text-sm"
                />
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Heure de fin
                </label>
                <input
                  type="time"
                  value={vehicleData.time_window_end}
                  onChange={(e) => handleChange('time_window_end', e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-primary focus:tw-border-primary tw-text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Section Breaks */}
        {!isLoading && (
          <div className="tw-mt-6">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <div className="tw-text-sm tw-font-medium tw-text-gray-700">
                Pauses configurées ({vehicleData.breaks.length})
              </div>
              <button
                type="button"
                onClick={handleAddBreak}
                className="tw-px-3 tw-py-1 tw-border-none tw-bg-main tw-text-white tw-text-sm tw-rounded-md hover:tw-bg-main-dark tw-transition-colors"
              >
                + Ajouter une pause
              </button>
            </div>
            
            {vehicleData.breaks.length > 0 ? (
              <div className="tw-space-y-4">
                {vehicleData.breaks.map((breakItem, index) => (
                  <div
                    key={breakItem.id || index}
                    className="tw-p-4 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">
                        Pause {index + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveBreak(index)}
                        className="tw-text-red-600 hover:tw-text-red-800 tw-text-sm"
                      >
                        ✕ Supprimer
                      </button>
                    </div>
                    
                    <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={breakItem.description}
                          onChange={(e) => handleBreakChange(index, 'description', e.target.value)}
                          placeholder="Ex: Pause déjeuner"
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          Début
                        </label>
                        <input
                          type="time"
                          value={breakItem.time_window_start}
                          onChange={(e) => handleBreakChange(index, 'time_window_start', e.target.value)}
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          Fin
                        </label>
                        <input
                          type="time"
                          value={breakItem.time_window_end}
                          onChange={(e) => handleBreakChange(index, 'time_window_end', e.target.value)}
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          Durée (secondes)
                        </label>
                        <input
                          type="number"
                          value={breakItem.service}
                          onChange={(e) => handleBreakChange(index, 'service', parseInt(e.target.value, 10) || 0)}
                          placeholder="1800"
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-text-center tw-py-6 tw-text-gray-500 tw-text-sm tw-border tw-border-dashed tw-border-gray-300 tw-rounded-lg">
                Aucune pause configurée
              </div>
            )}
          </div>
        )}

        {/* Section Skills */}
        {!isLoading && (
          <div className="tw-mt-6">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <div className="tw-text-sm tw-font-medium tw-text-gray-700">
                Compétences configurées ({vehicleData.skills.length})
              </div>
              <button
                type="button"
                onClick={handleAddSkill}
                className="tw-px-3 tw-py-1 tw-border-none tw-bg-main tw-text-white tw-text-sm tw-rounded-md hover:tw-bg-main-dark tw-transition-colors"
              >
                + Ajouter une compétence
              </button>
            </div>
            
            {vehicleData.skills.length > 0 ? (
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {vehicleData.skills.map((skill, index) => (
                  <div
                    key={skill.id || index}
                    className="tw-p-3 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">
                        Compétence {index + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(index)}
                        className="tw-text-red-600 hover:tw-text-red-800 tw-text-sm"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          ID Compétence
                        </label>
                        <input
                          type="number"
                          value={skill.skill_id}
                          onChange={(e) => handleSkillChange(index, 'skill_id', parseInt(e.target.value, 10) || 1)}
                          placeholder="1"
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={skill.description}
                          onChange={(e) => handleSkillChange(index, 'description', e.target.value)}
                          placeholder="Ex: Frigorifique"
                          className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-text-center tw-py-6 tw-text-gray-500 tw-text-sm tw-border tw-border-dashed tw-border-gray-300 tw-rounded-lg">
                Aucune compétence configurée
              </div>
            )}
          </div>
        )}
      </div>
    </TabPanel>
  );
});

Details.displayName = 'Details';

export default Details;
